import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as invoiceService from '@/services/invoice/invoiceService';
import type {
  Invoice,
  InvoiceFormData,
  InvoiceLineItemFormData,
  InvoicePaymentFormData,
  InvoiceTemplate,
  InvoiceSettings
} from '@/types/invoice.types';

// Invoice queries
export function useInvoices(filters?: Parameters<typeof invoiceService.fetchInvoices>[0]) {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => invoiceService.fetchInvoices(filters),
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => invoiceService.fetchInvoiceById(id),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InvoiceFormData) => invoiceService.createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create invoice');
      },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Invoice> }) =>
      invoiceService.updateInvoice(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update invoice');
      },
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceService.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete invoice');
      },
  });
}

// Line item mutations
export function useAddLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, item }: { invoiceId: string; item: InvoiceLineItemFormData }) =>
      invoiceService.addLineItem(invoiceId, item),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      toast.success('Line item added');
    },
    onError: (error) => {
      toast.error('Failed to add line item');
      },
  });
}

export function useUpdateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates, invoiceId }: { 
      id: string; 
      updates: Partial<InvoiceLineItemFormData>;
      invoiceId: string;
    }) => invoiceService.updateLineItem(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
    },
    onError: (error) => {
      toast.error('Failed to update line item');
      },
  });
}

export function useDeleteLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) =>
      invoiceService.deleteLineItem(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      toast.success('Line item removed');
    },
    onError: (error) => {
      toast.error('Failed to remove line item');
      },
  });
}

// Payment mutations
export function useAddPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, payment }: { invoiceId: string; payment: InvoicePaymentFormData }) =>
      invoiceService.addPayment(invoiceId, payment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Payment recorded successfully');
    },
    onError: (error) => {
      toast.error('Failed to record payment');
      },
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invoiceId }: { id: string; invoiceId: string }) =>
      invoiceService.deletePayment(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Payment removed');
    },
    onError: (error) => {
      toast.error('Failed to remove payment');
      },
  });
}

// Status mutations
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceService.sendInvoice(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice sent successfully');
    },
    onError: (error) => {
      toast.error('Failed to send invoice');
      },
  });
}

export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceService.cancelInvoice(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice cancelled');
    },
    onError: (error) => {
      toast.error('Failed to cancel invoice');
      },
  });
}

// Settings queries
export function useInvoiceSettings(organizationId: string) {
  return useQuery({
    queryKey: ['invoice-settings', organizationId],
    queryFn: () => invoiceService.fetchInvoiceSettings(organizationId),
    enabled: !!organizationId,
  });
}

export function useUpdateInvoiceSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, settings }: { 
      organizationId: string; 
      settings: Partial<InvoiceSettings> 
    }) => invoiceService.updateInvoiceSettings(organizationId, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-settings', variables.organizationId] });
      toast.success('Invoice settings updated');
    },
    onError: (error) => {
      toast.error('Failed to update settings');
      },
  });
}

// Template queries
export function useInvoiceTemplates(organizationId?: string) {
  return useQuery({
    queryKey: ['invoice-templates', organizationId],
    queryFn: () => invoiceService.fetchInvoiceTemplates(organizationId),
  });
}

export function useCreateInvoiceTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (template: Partial<InvoiceTemplate>) => 
      invoiceService.createInvoiceTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-templates'] });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create template');
      },
  });
}

export function useGenerateFromTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => 
      invoiceService.generateInvoiceFromTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice generated from template');
    },
    onError: (error) => {
      toast.error('Failed to generate invoice');
      },
  });
}